import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { METHOD_DEFINITIONS } from '../../constants/methods';
import LANDING_INTROS from '../../constants/landingIntros';
import { LogoIcon } from '../../Icon';

const ORBIT_RADII = [140, 200, 260];

const ORBIT_LAYOUT = [
  { methodId: 'gauss', radius: 140, angle: 0 },
  { methodId: 'linearfit', radius: 260, angle: 190 },
  { methodId: 'interpolation', radius: 275, angle: 125 },
  { methodId: 'iterlinsolve', radius: 140, angle: -210 },
  { methodId: 'decomposition', radius: 260, angle: -30 },
  { methodId: 'newton', radius: 200, angle: 70 },
  { methodId: 'quadrature', radius: 190, angle: -110 },
  { methodId: 'simplex', radius: 260, angle: 30 },
];

function polarToXY(radius, angleDeg) {
  const angle = (angleDeg * Math.PI) / 180;
  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
  };
}

function MethodBubble({ method, isSelected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`group relative flex origin-center items-center justify-center rounded-full border bg-white/15 backdrop-blur-md will-change-transform transform-gpu
        ${isSelected
          ? 'h-32 w-32 sm:h-36 sm:w-36 lg:h-40 lg:w-40 border-white/40 z-20'
          : 'h-20 w-20 border-white/30 hover:scale-125 z-10'
        }`}
      style={{
        boxShadow: isSelected
          ? `0 45px 90px -35px ${method.color}`
          : `0 18px 35px -14px ${method.color}`,
        transition: 'all 700ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <span
        className={`flex items-center justify-center rounded-full text-white shadow-inner transform-gpu
          ${isSelected ? 'h-24 w-24 sm:h-28 sm:w-28 lg:h-32 lg:w-32' : 'h-16 w-16'}`}
        style={{
          backgroundColor: method.color,
          transition: 'all 700ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <span
          className="inline-flex items-center justify-center transform-gpu"
          style={{
            transform: isSelected ? 'scale(1.9)' : 'scale(1)',
            transition: 'transform 700ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {method.icon}
        </span>
      </span>
    </button>
  );
}

export default function LandingPage({ setActiveTab }) {
  const methodsById = useMemo(
    () => Object.fromEntries(METHOD_DEFINITIONS.map((def) => [def.id, def])),
    []
  );

  const [orbitScale, setOrbitScale] = useState(1);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [introVisible, setIntroVisible] = useState(false);
  const introTimeoutRef = useRef(null);

  useEffect(() => {
    const updateScale = () => {
      if (typeof window === 'undefined') return;
      const width = window.innerWidth;
      if (width < 480) setOrbitScale(0.55);
      else if (width < 768) setOrbitScale(0.65);
      else if (width < 1024) setOrbitScale(0.85);
      else setOrbitScale(1);
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  const handleBubbleClick = useCallback((methodId) => {
    if (selectedMethod === methodId) {
      // Clicking the centered bubble deselects it (same as back)
      setIntroVisible(false);
      if (introTimeoutRef.current) clearTimeout(introTimeoutRef.current);
      introTimeoutRef.current = setTimeout(() => {
        setSelectedMethod(null);
      }, 100);
      return;
    }

    // Hide intro text immediately for crossfade
    setIntroVisible(false);
    if (introTimeoutRef.current) clearTimeout(introTimeoutRef.current);

    // Set selected method (triggers position animation)
    setSelectedMethod(methodId);

    // Fade in the new intro text after a short delay
    introTimeoutRef.current = setTimeout(() => {
      setIntroVisible(true);
    }, 150);
  }, [selectedMethod]);

  const handleBackClick = useCallback(() => {
    setIntroVisible(false);
    if (introTimeoutRef.current) clearTimeout(introTimeoutRef.current);
    introTimeoutRef.current = setTimeout(() => {
      setSelectedMethod(null);
    }, 100);
  }, []);

  useEffect(() => {
    return () => {
      if (introTimeoutRef.current) clearTimeout(introTimeoutRef.current);
    };
  }, []);

  const orbitContainerSize = 540 * orbitScale;

  const selectedDef = selectedMethod ? methodsById[selectedMethod] : null;

  return (
    <section className="relative isolate flex min-h-[90vh] w-full flex-col justify-center overflow-hidden px-6 pt-16 pb-96 text-white lg:px-16">

      {/* Dekor */}
      <div className="pointer-events-none absolute -left-16 top-28 h-96 w-96 rounded-full bg-[#8b5cf6]/40 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-[#ec4899]/35 blur-[160px]" />
      <div className="pointer-events-none absolute inset-x-0 -bottom-40 h-96 bg-gradient-to-t from-[#0f1328]/70 to-transparent" />

      <div className="relative z-10 mx-auto flex max-w-6xl flex-col gap-32 lg:flex-row lg:items-center lg:justify-between">
        {/* Textseite */}
        <div className="max-w-2xl space-y-8 relative">
          {/* Default landing text */}
          <div
            style={{
              transition: 'opacity 500ms ease, transform 500ms ease',
              opacity: selectedMethod ? 0 : 1,
              transform: selectedMethod ? 'translateY(-10px)' : 'translateY(0)',
              pointerEvents: selectedMethod ? 'none' : 'auto',
              position: selectedMethod ? 'absolute' : 'relative',
              inset: selectedMethod ? 0 : undefined,
            }}
          >
            <p className="text-sm uppercase tracking-[0.35em] text-white/60">
              Numerical Methods Playground
            </p>
            <h2 className="text-4xl font-semibold leading-tight text-white md:text-3xl lg:text-5xl mt-4">
              Explore · Understand · Solve
            </h2>
            <p className="text-lg text-white/70 md:text-xl mt-6">
              Explore interactive visualizations and step-by-step explanations of classical numerical methods.
              From solving linear systems and Newton's method to integration and interpolation, see how each method works,
              compare approaches, and build deeper intuition — all in your browser.
            </p>
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center mt-8">
              <button
                onClick={() => setActiveTab('methodPicker')}
                className="group relative w-full max-w-full overflow-hidden rounded-full border border-white/20 bg-gradient-to-r from-[#8b5cf6] via-[#a855f7] to-[#ec4899] px-8 py-3 text-center text-base font-semibold text-white shadow-[0_0_35px_rgba(168,85,247,0.45)] transition duration-300 hover:shadow-[0_0_45px_rgba(236,72,153,0.6)] sm:max-w-[220px]"
              >
                <span className="relative z-10">Start Exploring</span>
                <span className="absolute inset-0 -z-10 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              </button>
              <span className="text-sm text-white/60">
                Pick a method and try the interactive workspace.
              </span>
            </div>
          </div>

          {/* Method intro text */}
          <div
            style={{
              transition: 'opacity 500ms ease, transform 500ms ease',
              opacity: selectedMethod && introVisible ? 1 : 0,
              transform: selectedMethod && introVisible ? 'translateY(0)' : 'translateY(15px)',
              pointerEvents: selectedMethod ? 'auto' : 'none',
              position: selectedMethod ? 'relative' : 'absolute',
              inset: selectedMethod ? undefined : 0,
            }}
          >
            {selectedDef && (
              <>
                <button
                  onClick={handleBackClick}
                  className="flex items-center gap-2 text-sm text-white/50 hover:text-white/80 transition-colors duration-200 mb-4"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <h2
                  className="text-3xl font-semibold leading-tight text-white md:text-2xl lg:text-4xl mt-4"
                  style={{ color: selectedDef.color }}
                >
                  {selectedDef.name}
                </h2>
                <p className="text-lg text-white/70 md:text-xl mt-6 text-justify hyphens-auto leading-relaxed">
                  {LANDING_INTROS[selectedMethod]}
                </p>
                <div className="flex flex-col gap-6 sm:flex-row sm:items-center mt-8">
                  <button
                    onClick={() => setActiveTab(selectedMethod)}
                    className="group relative w-full max-w-full overflow-hidden rounded-full border border-white/20 px-8 py-3 text-center text-base font-semibold text-white shadow-[0_0_35px_rgba(168,85,247,0.45)] transition duration-300 hover:shadow-[0_0_45px_rgba(236,72,153,0.6)] sm:max-w-[260px]"
                    style={{
                      background: `linear-gradient(135deg, ${selectedDef.color}, ${selectedDef.color}dd)`,
                    }}
                  >
                    <span className="relative z-10">Open Workspace</span>
                    <span className="absolute inset-0 -z-10 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Orbit */}
        <div
          className="relative mx-auto hidden w-full max-w-full items-center justify-center md:flex"
          style={{
            height: `${orbitContainerSize}px`,
            width: `${orbitContainerSize}px`,
          }}
        >
          {/* Orbit rings */}
          {ORBIT_RADII.map((radius) => (
            <div
              key={radius}
              className="absolute rounded-full border border-white/35"
              style={{
                width: `${radius * 2 * orbitScale}px`,
                height: `${radius * 2 * orbitScale}px`,
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          ))}

          {/* Method bubbles */}
          {ORBIT_LAYOUT.map((entry) => {
            const method = methodsById[entry.methodId];
            if (!method) return null;

            const isSelected = selectedMethod === entry.methodId;
            const pos = polarToXY(entry.radius * orbitScale, entry.angle);

            return (
              <div
                key={entry.methodId}
                className="absolute left-1/2 top-1/2"
                style={{
                  transform: isSelected
                    ? 'translate(-50%, -50%) translate(0px, 0px)'
                    : `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px)`,
                  transition: 'transform 700ms cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: isSelected ? 20 : 10,
                }}
              >
                <MethodBubble
                  method={method}
                  isSelected={isSelected}
                  onClick={() => handleBubbleClick(entry.methodId)}
                />
              </div>
            );
          })}

          {/* Center logo - fades out when a method is selected */}
          <div
            className="absolute left-1/2 top-1/2 flex h-32 w-32 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-white/40 bg-white/15 text-white shadow-[0_45px_90px_-35px_rgba(139,92,246,0.8)] backdrop-blur-md sm:h-36 sm:w-36 lg:h-40 lg:w-40"
            style={{
              transition: 'opacity 500ms ease, transform 500ms ease',
              opacity: selectedMethod ? 0 : 1,
              transform: `translate(-50%, -50%) scale(${selectedMethod ? 0.6 : 1})`,
              pointerEvents: selectedMethod ? 'none' : 'auto',
            }}
          >
            <LogoIcon className="h-20 w-20 sm:h-24 sm:w-24 lg:h-20 lg:w-20" />
          </div>
        </div>
      </div>
    </section>
  );
}
